/**
 * VEEZE subscription box builder.
 *
 * The interval buttons and the per-cheese steppers drive one summary, and
 * "ABO STARTEN" posts every chosen cheese to the cart in a single request, each
 * line carrying the selected selling plan.
 *
 * Prices are never re-derived here. Shopify pre-computes a price per variant per
 * selling plan (`selling_plan_allocations`), and the section renders those into
 * the JSON this reads — so what the panel shows is what the cart will charge,
 * including any rounding Shopify applies. Re-applying "minus 15 percent" in JS
 * would drift from the cart the moment a plan changed.
 */
class VeezeSubscription extends HTMLElement {
  connectedCallback() {
    const payload = this.querySelector('[data-sub-data]');
    if (!payload) return;

    try {
      this.data = JSON.parse(payload.textContent);
    } catch (error) {
      return;
    }

    this.plan = this.data.plans.find((p) => p.popular) || this.data.plans[0];
    if (!this.plan) return;

    /* variantId -> quantity, seeded from each row's own starting amount. */
    this.qty = new Map();
    this.data.products.forEach((p) => this.qty.set(String(p.variant), p.start || 0));

    this.format = this.data.moneyFormat || '{{amount_with_comma_separator}} €';

    this.addEventListener('click', this.onClick.bind(this));
    this.render();
  }

  onClick(event) {
    const interval = event.target.closest('[data-weeks]');
    if (interval) {
      const next = this.data.plans.find(
        (p) => String(p.weeks) === interval.dataset.weeks
      );
      if (next) {
        this.plan = next;
        this.render();
      }
      return;
    }

    const step = event.target.closest('[data-step]');
    if (step) {
      const key = step.dataset.variant;
      const delta = Number(step.dataset.step);
      const max = Number(step.dataset.max || 99);
      const now = this.qty.get(key) || 0;
      this.qty.set(key, Math.min(max, Math.max(0, now + delta)));
      this.render();
      return;
    }

    if (event.target.closest('[data-start]')) this.submit();
  }

  /*
   * The shop's own money format rather than `Intl.NumberFormat`. This store's
   * locale is `en`, which would render "€21.97" where the design and the rest of
   * the storefront both show "21,97 €" — and the format string is what every
   * `| money` filter in the theme already uses, so the panel cannot disagree
   * with the cart.
   */
  money(cents) {
    const amount = (cents / 100).toFixed(2);
    const [whole, part] = amount.split('.');
    const group = (sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

    return this.format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      switch (token) {
        case 'amount_no_decimals':
          return group(',');
        case 'amount_with_comma_separator':
          return `${group('.')},${part}`;
        case 'amount_no_decimals_with_comma_separator':
          return group('.');
        case 'amount_with_apostrophe_separator':
          return `${group("'")}.${part}`;
        default:
          return `${group(',')}.${part}`;
      }
    });
  }

  /* Totals come from the allocation for the selected plan, summed per line. */
  totals() {
    let count = 0;
    let regular = 0;
    let planned = 0;

    this.data.products.forEach((p) => {
      const q = this.qty.get(String(p.variant)) || 0;
      if (!q) return;
      count += q;
      regular += q * p.price;
      const allocation = p.allocations[String(this.plan.id)];
      planned += q * (allocation == null ? p.price : allocation);
    });

    return { count, regular, planned, saved: regular - planned };
  }

  render() {
    const t = this.totals();

    this.querySelectorAll('[data-weeks]').forEach((button) => {
      const on = button.dataset.weeks === String(this.plan.weeks);
      button.setAttribute('aria-pressed', String(on));
    });

    this.querySelectorAll('[data-count-for]').forEach((el) => {
      el.textContent = String(this.qty.get(el.dataset.countFor) || 0);
    });

    /* A stepper at zero cannot go lower; at its cap it cannot go higher. */
    this.querySelectorAll('[data-step]').forEach((button) => {
      const q = this.qty.get(button.dataset.variant) || 0;
      const max = Number(button.dataset.max || 99);
      button.disabled =
        Number(button.dataset.step) < 0 ? q <= 0 : q >= max;
    });

    const set = (name, value) => {
      const el = this.querySelector(`[data-sum="${name}"]`);
      if (el) el.textContent = value;
    };

    /* Two digits, as the frame shows "03" rather than "3". */
    set('count', String(t.count).padStart(this.data.countPad || 2, '0'));
    set('interval', (this.data.everyLabel || '[weeks] Wochen').replace(
      '[weeks]', String(this.plan.weeks)
    ));
    set('subtotal', this.money(t.regular));
    set('discount', t.saved ? '−' + this.money(t.saved) : this.money(0));
    set('total', this.money(t.planned));

    const label = this.querySelector('[data-discount-label]');
    if (label) {
      label.textContent = (this.data.discountLabel || 'Abo-Rabatt (−[percent] %)')
        .replace('[percent]', String(this.plan.discount));
    }

    const save = this.querySelector('[data-save-text]');
    if (save) {
      save.textContent = (this.data.saveText || '★ Du sparst [percent] % im Abo')
        .replace('[percent]', String(this.plan.discount));
    }

    /* Nothing chosen means nothing to subscribe to. */
    const start = this.querySelector('[data-start]');
    if (start) start.setAttribute('aria-disabled', String(t.count === 0));

    this.error('');
  }

  error(message) {
    const el = this.querySelector('[data-error]');
    if (el) el.textContent = message;
  }

  async submit() {
    const items = [];
    this.data.products.forEach((p) => {
      const q = this.qty.get(String(p.variant)) || 0;
      if (q > 0) items.push({ id: p.variant, quantity: q, selling_plan: this.plan.id });
    });

    if (!items.length) {
      this.error(this.data.emptyError || 'Wähle mindestens einen Käse aus.');
      return;
    }

    const start = this.querySelector('[data-start]');
    start?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${this.data.routes.add}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || response.statusText);
      }

      window.location.href = this.data.routes.cart;
    } catch (error) {
      this.error(error.message || this.data.addError || 'Das hat nicht funktioniert.');
    } finally {
      start?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('veeze-subscription')) {
  customElements.define('veeze-subscription', VeezeSubscription);
}
